import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import SectionHeader from "@/components/base/SectionHeader";
import usePageMeta from "@/hooks/usePageMeta";
import { formatHeat, getMovieById, getRelatedMovies } from "@/utils/movie";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const [notice, setNotice] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setNotice(false);
  }, [id]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(false), 2600);
    return () => clearTimeout(timer);
  }, [notice]);

  usePageMeta({
    title: movie ? `${movie.title} - 蓝莓视频` : "影视详情 - 蓝莓视频",
    description: movie
      ? `${movie.title}（${movie.year}）${movie.description.slice(0, 60)}……蓝莓视频为你提供作品详情与相关推荐。`
      : "蓝莓视频影视作品详情页。",
    keywords: movie
      ? `蓝莓视频,${movie.title},${movie.tags.join(",")},影视详情`
      : "蓝莓视频,影视详情",
  });

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-16">
          <div className="flex flex-col items-center justify-center py-32 px-4 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-film-line text-3xl"></i>
            </span>
            <h1 className="text-xl font-medium text-foreground-900 mb-2">
              抱歉，未找到该影视作品
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              作品可能已下线，或链接有误
            </p>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const typeLabel = movie.type === "movie" ? "电影" : "电视剧";
  const categoryTo =
    movie.type === "movie" ? "/category/movie" : "/category/tvseries";

  const metaItems = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "国家/地区", value: movie.region },
    { label: "时长", value: movie.duration },
    { label: "更新日期", value: movie.updateDate },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16">
        <section className="relative">
          <div className="absolute inset-0 h-[280px] md:h-[360px] overflow-hidden">
            <img
              src={movie.cover}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover object-top blur-2xl scale-110 opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/40 to-background-50"></div>
          </div>

          <div className="relative px-4 md:px-8 pt-8 md:pt-12 pb-8">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link
                to={categoryTo}
                className="hover:text-accent-500 transition-colors cursor-pointer"
              >
                {typeLabel}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">{movie.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="w-[160px] sm:w-[200px] md:w-[260px] shrink-0 mx-auto md:mx-0">
                <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200/70">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 封面`}
                    title={`${movie.title} - 蓝莓视频`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950">
                    {movie.title}
                  </h1>
                  <span className="flex items-center gap-1 text-accent-500 font-heading font-semibold text-xl">
                    <i className="ri-star-fill text-base"></i>
                    {movie.rating.toFixed(1)}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs text-foreground-800 bg-background-100 border border-background-200/70 px-2.5 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  <span className="flex items-center gap-1 text-xs text-foreground-600">
                    <i className="ri-fire-line text-accent-400"></i>
                    {formatHeat(movie.heat)}次播放
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-6">
                  {metaItems.map((item) => (
                    <div key={item.label} className="flex items-start gap-3 text-sm">
                      <span className="shrink-0 w-20 text-foreground-500">{item.label}</span>
                      <span className="text-foreground-900">
                        {item.value && item.value.trim() ? item.value : "暂无信息"}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setNotice(true)}
                    className="flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    立即观看
                  </button>
                  <Link
                    to={categoryTo}
                    className="flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm whitespace-nowrap hover:border-primary-400 transition-colors cursor-pointer"
                  >
                    <i className="ri-apps-2-line text-lg"></i>
                    浏览同类{typeLabel}
                  </Link>
                </div>

                {notice && (
                  <div className="mt-4 flex items-center gap-2 bg-background-100 border border-background-300 text-foreground-900 text-sm px-4 py-3 rounded-lg animate-fade-in">
                    <i className="ri-information-line text-accent-500 text-lg"></i>
                    演示站点暂不提供真实播放，影片资源仅供信息展示
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-8 py-6 md:py-8">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              剧情简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-4xl">
              {movie.description}
            </p>
          </div>
        </section>

        <section className="px-4 md:px-8 pb-14 md:pb-20">
          <SectionHeader
            icon="ri-magic-line"
            title="相关推荐"
            subtitle="根据类型与标签为你精选"
            moreTo={categoryTo}
            moreLabel={`更多${typeLabel}`}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}